/**
 * Presentation rules for step 4 (f10.7) -- labels, pills and the "what the
 * system did" list. Nothing here decides anything: cleaning, keys, matching,
 * figures and every capability come from the server.
 */

import type { RateCell, SalesRateReview } from '@/api/client';

export {
  hasStagedItems,
  isParsing,
  refusalMessage,
  severityPill,
} from '../usEngagements/usEngagementsView';
export { MONTH_LABELS } from '../billableDays/billableDaysView';
export { compactDecimal, decisionPill } from '../kimbleDump/kimbleDumpView';

export const UNDECIDED = 'UNDECIDED';
export const MATCHED = 'MATCHED';
export const STATE_ERROR = 'ERROR';
export const STATE_BLANK = 'BLANK';

export interface Transformation {
  ok: boolean;
  text: string;
}

/** How a rate cell reads: a number, "—" for blank, a warning for an error. Never "0" for a null. */
export function cellKind(cell: RateCell): 'value' | 'blank' | 'error' {
  if (cell.state === STATE_ERROR) return 'error';
  if (cell.rate === null || cell.state === STATE_BLANK) return 'blank';
  return 'value';
}

/** "What the system did with it", stated from the batch's own facts (design D10). */
export function transformations(review: SalesRateReview): Transformation[] {
  const { batch, figures } = review;
  const list: Transformation[] = [
    {
      ok: true,
      text:
        batch.detected_header_row === null
          ? 'Header located by content'
          : `Header found at row ${batch.detected_header_row} — Element: Reference and Resource BU located by content`,
    },
    { ok: true, text: `Month columns bound for ${review.period_year}; the annual Total copied, never computed` },
    {
      ok: true,
      text: `Rates in ${batch.scope?.currency_code ?? 'the scope currency'} (from the scope) — nothing converted`,
    },
  ];
  if (figures.rows_forward_filled > 0) {
    list.push({ ok: true, text: `${figures.rows_forward_filled} blank element references filled from the row above` });
  }
  const errors = figures.cells.ERROR ?? 0;
  if (errors > 0) {
    list.push({ ok: false, text: `${errors} error cells kept as missing rates — never zero` });
  }
  if (figures.unmapped_bu_values > 0) {
    list.push({
      ok: false,
      text: `${figures.unmapped_bu_values} Resource BU values matched no mapping (${figures.unmapped_bu_rows} rows)`,
    });
  }
  if (figures.duplicate_keys > 0) {
    list.push({ ok: false, text: `${figures.duplicate_keys} rows repeat a key; the first row in file order is used` });
  }
  if (batch.rejection_reason) list.push({ ok: false, text: batch.rejection_reason });
  return list;
}
